import { useCallback, useContext, useEffect, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { BillingData, CardForm, CompanyPosition, Subscription } from '@vyce/core/src/types';
import { useBooleanState, useQuery } from '@vyce/core/src/hooks';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import {
  cancelSubscriptionRequest,
  createSubscriptionRequest,
  getBillingDataRequest,
  removeMessagingSubscriptionRequest,
  addFormsSubscriptionRequest,
  removeFormsSubscriptionRequest,
  resumeSubscriptionRequest,
  getSubscriptionDataRequest,
} from '@vyce/core/src/api/billing';
import { NotificationContext, getErrorTitle } from '@vyce/core/src/contexts/notificationContext';
import { NOT_SCHEDULED_FOR_CANCELLATION_TITLE } from '@vyce/core/src/constants';
import { formatUtcToUkDate } from '@vyce/core/src/utils/dates';
import { addMessagingSubscriptionRequest } from '@vyce/core/src/api/billing';
import { syncCompanyChatsUsersRequest } from '@vyce/core/src/api/chat';

import { useActions, useTypedSelector } from '../../../hooks';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { locationToSite } from '@vyce/core/src/modules/timeModule/utils';

export const useSubscriptionData = () => {
  const { selectedCompany, access_token, selectedCompanyAppData } = useTypedSelector(state => state.helper);
  const { email, uuid, phone, first_name, last_name, positions } = useTypedSelector(state => state.user);
  const { updateSelectedCompanySubscription, fetchCompanyData, setPosition } = useActions();
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [resumeLoading, setResumeLoading] = useState<boolean>(false);
  const [isSuccessDialogOpen, openSuccessDialog, closeSuccessDialog] = useBooleanState(false);
  const [isManageDialogOpen, openManageDialog, closeManageDialog] = useBooleanState(false);
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const [isFormsSubscriptionDialogOpen, openFormsSubscriptionDialog, closeFormsSubscriptionDialog] =
    useBooleanState(false);
  const [
    isRemoveFormsSubscriptionDialogOpen,
    openRemoveFormsSubscriptionDialog,
    closeRemoveFormsSubscriptionDialog,
  ] = useBooleanState(false);
  const [isCancelReasonDialogOpen, openCancelReasonDialog, closeCancelReasonDialog] = useBooleanState(false);
  const [isActivateSubscriptionDialogOpen, openActivateSubscriptionDialog, closeActivateSubscriptionDialog] =
    useBooleanState(false);
  const [isLoginDialogOpen, openLoginDialog, closeLoginDialog] = useBooleanState(false);
  const [loadingMessagingSubscription, setLoadingMessagingSubscription] = useState<boolean>(false);
  const [loadingFormsSubscription, setLoadingFormsSubscription] = useState<boolean>(false);
  const [messagingSubscription, setMessagingSubscription] = useState<boolean>(false);
  const [formsSubscription, setFormsSubscription] = useState<boolean>(false);

  const [
    isMessagingSubscriptionDialogOpen,
    openMessagingSubscriptionDialog,
    closeMessagingSubscriptionDialog,
  ] = useBooleanState(false);
  const [
    isRemoveMessagingSubscriptionDialogOpen,
    openRemoveMessagingSubscriptionDialog,
    closeRemoveMessagingSubscriptionDialog,
  ] = useBooleanState(false);
  const { syncCompanyChats } = useChatRequests({
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
  });
  const history = useHistory();
  const [billingData, setBillingData] = useState<BillingData>();
  const query = useQuery();

  const subscribe = useCallback(
    async (data: CardForm) => {
      if (!selectedCompany?.uuid) {
        return;
      }
      setLoading(true);
      try {
        const res = await createSubscriptionRequest(access_token, selectedCompany.uuid, data);
        const subscription: Subscription = res.data;
        setLoading(false);
        closeActivateSubscriptionDialog();
        if (updateSelectedCompanySubscription) {
          updateSelectedCompanySubscription(subscription);
        }
        fetchCompanyData(selectedCompany.uuid);
        openSuccessDialog();
      } catch (e) {
        handleServerError(e);
        setLoading(false);
      }
    },
    [selectedCompany, access_token, updateSelectedCompanySubscription]
  );

  const addMessagingSubscription = async (): Promise<boolean> => {
    if (!selectedCompany?.uuid) {
      return false;
    }
    try {
      setLoadingMessagingSubscription(true);
      await addMessagingSubscriptionRequest(selectedCompany.uuid);
      setLoadingMessagingSubscription(false);
      fetchCompanyData(selectedCompany.uuid);
      syncCompanyChatsUsersRequest(selectedCompany.uuid);
      syncCompanyChats({
        companyId: selectedCompany.uuid,
        companyName: selectedCompany.name || '',
        teams: selectedCompanyAppData?.teams || [],
        locations: (selectedCompanyAppData?.locations || []).map(location => locationToSite(location)),
      });
      return true;
    } catch (error) {
      handleServerError(error);
      setLoadingMessagingSubscription(false);
      return false;
    } finally {
      closeMessagingSubscriptionDialog();
    }
  };

  const removeMessagingSubscription = async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      setLoadingMessagingSubscription(true);
      await removeMessagingSubscriptionRequest(selectedCompany.uuid);
      setLoadingMessagingSubscription(false);
      fetchCompanyData(selectedCompany.uuid);
    } catch (error) {
      handleServerError(error);
      setLoadingMessagingSubscription(false);
    } finally {
      closeRemoveMessagingSubscriptionDialog();
    }
  };

  const addFormsSubscription = async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      setLoadingFormsSubscription(true);
      await addFormsSubscriptionRequest(selectedCompany.uuid);
      setLoadingFormsSubscription(false);
      fetchCompanyData(selectedCompany.uuid);
    } catch (error) {
      handleServerError(error);
      setLoadingFormsSubscription(false);
    } finally {
      closeFormsSubscriptionDialog();
    }
  };

  const removeFormsSubscription = async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      setLoadingFormsSubscription(true);
      await removeFormsSubscriptionRequest(selectedCompany.uuid);
      setLoadingFormsSubscription(false);
      fetchCompanyData(selectedCompany.uuid);
    } catch (error) {
      handleServerError(error);
      setLoadingFormsSubscription(false);
    } finally {
      closeFormsSubscriptionDialog();
    }
  };

  const getBillingData = async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      const res = await getBillingDataRequest({ token: access_token, companyId: selectedCompany.uuid });
      if (!isMounted.current) return;
      setBillingData(res.data);
    } catch (e: any) {
      console.error(e);
      if (!isMounted.current) return;
      setBillingData(undefined);
      // No billing rights for this company: say so rather than showing an empty invoice silently.
      if (e?.response?.status === 403) handleServerError(e);
    }
  };

  const cancelSubscription = useCallback(async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    try {
      // The subscription stays active until current_period_end; the response already says so.
      const res = await cancelSubscriptionRequest(access_token, selectedCompany.uuid);
      const subscription: Subscription = res.data;
      if (updateSelectedCompanySubscription) {
        updateSelectedCompanySubscription(subscription);
      }
      closeConfirmDialog();
      openCancelReasonDialog();
    } catch (e) {
      handleServerError(e);
    }
  }, [selectedCompany, access_token]);

  const refreshSubscription = async (companyId: string) => {
    const res = await getSubscriptionDataRequest({ token: access_token, companyId });
    if (updateSelectedCompanySubscription) {
      updateSelectedCompanySubscription(res.data);
    }
  };

  const resumeSubscription = async () => {
    if (!selectedCompany?.uuid) {
      return;
    }
    setResumeLoading(true);
    try {
      // Same subscription carries on: no new card, no new trial.
      await resumeSubscriptionRequest(access_token, selectedCompany.uuid);
      await refreshSubscription(selectedCompany.uuid);
      showNotification({ message: 'Your subscription continues.', options: { variant: 'success' } });
    } catch (e) {
      // 409 here means nothing was pending, so there is nothing to undo: just show the real state.
      if (getErrorTitle(e) === NOT_SCHEDULED_FOR_CANCELLATION_TITLE) {
        try {
          await refreshSubscription(selectedCompany.uuid);
        } catch (refreshError) {
          console.error(refreshError);
        }
      } else {
        handleServerError(e);
      }
    } finally {
      if (isMounted.current) setResumeLoading(false);
    }
  };

  const subscriptionEndDate = formatUtcToUkDate(selectedCompanyAppData?.subscription?.current_period_end);

  const handleNext = () => {
    history.push('/subscriptions/billing/payment-details');
  };

  useEffect(() => {
    if (selectedCompany) {
      getBillingData();
      const companyId = query.get('companyId');
      const companyPosition = positions?.find(position => position.company.uuid === companyId);
      if (companyPosition) {
        setPosition(companyPosition as CompanyPosition);
      }
      if (query.has('dialog')) {
        openActivateSubscriptionDialog();
      }
    }
  }, [selectedCompany]);

  useEffect(() => {
    setMessagingSubscription(!!selectedCompanyAppData?.messagingSubscription);
  }, [selectedCompanyAppData]);

  return {
    handleNext,
    cancelSubscription,
    resumeSubscription,
    resumeLoading,
    subscriptionEndDate,
    subscribe,
    openActivateSubscriptionDialog,
    openManageDialog,
    isSuccessDialogOpen,
    closeSuccessDialog,
    closeConfirmDialog,
    isConfirmDialogOpen,
    closeCancelReasonDialog,
    isCancelReasonDialogOpen,
    isActivateSubscriptionDialogOpen,
    closeActivateSubscriptionDialog,
    loading,
    openConfirmDialog,
    isManageDialogOpen,
    openLoginDialog,
    closeManageDialog,
    isLoginDialogOpen,
    closeLoginDialog,
    selectedCompany,
    email,
    selectedCompanyAppData,
    billingData,
    isMessagingSubscriptionDialogOpen,
    openMessagingSubscriptionDialog,
    closeMessagingSubscriptionDialog,
    addMessagingSubscription,
    loadingMessagingSubscription,
    isRemoveMessagingSubscriptionDialogOpen,
    openRemoveMessagingSubscriptionDialog,
    closeRemoveMessagingSubscriptionDialog,
    removeMessagingSubscription,
    messagingSubscription,
    isFormsSubscriptionDialogOpen,
    openFormsSubscriptionDialog,
    closeFormsSubscriptionDialog,
    addFormsSubscription,
    loadingFormsSubscription,
    formsSubscription,
    openRemoveFormsSubscriptionDialog,
    closeRemoveFormsSubscriptionDialog,
    removeFormsSubscription,
  };
};
